import { ReferenceListName } from '@/utils/reference-lists/reference-lists.interface';
import { ReferenceListsService } from '@/utils/reference-lists/reference-lists.service';
import { ChangeDetectionStrategy, Component, type OnInit, inject } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { BuildingIndicatorCode } from '../../../constants/building-indicator.constants';
import { LAST_TWELVE_MONTHS_PERIOD } from '../../../constants/period.constant';
import type { BuildingId, Period } from '../../../interfaces/Building.interface';
import type { LegacyBuildingsIndicatorsRequest } from '../../../interfaces/BuildingIndicators.interface';
import { BuildingsIndicatorsProvider } from '../../../providers/buildings-indicators/buildings-indicators.provider';
import { BuildingsProvider } from '../../../providers/buildings/buildings.provider';
import { DashboardingProvider } from '../../../providers/dashboarding.provider';
import { WidgetSize } from '../../../providers/widgets.contants';
import { RoutingService } from '../../../services/routing/routing.service';
import { DeveloperHelperComponent } from '../../developer-helper/developer-helper';
import { LoadingAndErrorComponent } from '../../loading-and-error/loading-and-error';
import { AbstractWidgetClass } from '../AbstractWidget.class';
import { WidgetCardComponent } from '../widget-card/widget-card';
import { OldDataSourceCode } from './../../../services/developer-helper.service';

interface KPI {
    buildingId: BuildingId;
    name: string;
    epiValue: number;
    epiDisplayUnit: string | null;
}

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./widget-building-performant-sites.scss'],
    selector: 'widget-building-performant-sites',
    templateUrl: 'widget-building-performant-sites.html',
    imports: [WidgetCardComponent, DeveloperHelperComponent, LoadingAndErrorComponent, TranslatePipe]
})
export class WidgetBuildingPerformantSitesComponent extends AbstractWidgetClass<null, void> implements OnInit {
    private _routingService = inject(RoutingService);
    private _buildingsProvider = inject(BuildingsProvider);
    private _dashboardingProvider = inject(DashboardingProvider);
    private _buildingIndicatorsProvider = inject(BuildingsIndicatorsProvider);
    private _referenceListsService = inject(ReferenceListsService);

    size = WidgetSize.HALF;

    sitesTop: KPI[] = [];
    sitesFlop: KPI[] = [];
    period!: Period;
    private _buildingsEpi: Map<BuildingId, number> = new Map();
    private _defaultIndicatorCode: BuildingIndicatorCode = BuildingIndicatorCode.ENERGY_CONSUMPTION_BY_SURFACE;

    lastTwelveMonths = LAST_TWELVE_MONTHS_PERIOD;

    isLoading: boolean = true;
    loadingError: boolean = false;

    OldDataSourceCode = OldDataSourceCode;

    ngOnInit() {
        const allEnergyUniverseIds = this._buildingIndicatorsProvider.getReferenceUniverseIdsByIndicatorCode(this._defaultIndicatorCode);

        this._dashboardingProvider
            .getMeaningfulNodeIdAndSelectedPeriod$()
            .pipe(untilDestroyed(this))
            .subscribe(([nodeId, period]) => {
                this.period = period;
                this.isLoading = true;
                this.loadingError = false;

                const dataRequest: LegacyBuildingsIndicatorsRequest = {
                    classificationNodeId: nodeId,
                    universeIds: allEnergyUniverseIds,
                    year: Number(this.period.name),
                    indicators: [{ indicatorCode: this._defaultIndicatorCode }]
                };

                this._buildingIndicatorsProvider
                    .fetchBuildingsIndicatorsData(dataRequest)
                    .then(([buildingIndicatorYear]) => {
                        if (buildingIndicatorYear) {
                            this._buildingsEpi = new Map();
                            buildingIndicatorYear.forEach(data => {
                                const buildingEpiValue = this._buildingsEpi.get(data.buildingId) || 0;
                                this._buildingsEpi.set(data.buildingId, buildingEpiValue + data.value);
                            });
                        }
                        this.filterTopAndFlop();
                        this.isLoading = false;
                    })
                    .catch(error => {
                        this.isLoading = false;
                        this.loadingError = true;
                        throw error;
                    });
            });
    }

    filterTopAndFlop() {
        if (this.period && Array.isArray(Object.keys(this._buildingsEpi))) {
            const indicator = this._buildingIndicatorsProvider.getBuildingIndicatorByIndicatorCode(this._defaultIndicatorCode);
            const unit = indicator ? this._referenceListsService.getReferenceListItemR(ReferenceListName.UNIT, indicator.unitCode) : null;

            const allSites = Array.from(this._buildingsEpi.entries())
                .map(([buildingId, epiValue]) => {
                    return {
                        buildingId,
                        epiValue,
                        name: this._buildingsProvider.getBuilding(buildingId).name,
                        epiDisplayUnit: unit ? unit.label : null
                    };
                })
                .sort(function (a, b) {
                    return a.epiValue - b.epiValue;
                });

            if (allSites.length > 3) {
                this.sitesTop = allSites.slice(0, 3);
                this.sitesFlop = allSites.reverse().slice(0, 3);
            } else {
                this.sitesTop = allSites.slice();
                this.sitesFlop = allSites.reverse().slice();
            }
        }
    }

    trunc(valeur: number): number {
        return Math.trunc(valeur);
    }

    goToBuildingHome(buildingUid: BuildingId) {
        this._routingService.resetNavigationToHomePage(buildingUid);
    }
}
